import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(isBetween);
import {
  EnumAppDocFormat,
  EnumAppDocType,
  EnumAppSignatureFormat,
  EnumAppSignatureType,
  EnumAppStatus,
  EnumAppSubmitted,
  EnumFileStatus,
  MApplication,
  MAppMeta,
  MAppSignature,
  MProfile
} from './scholarship.models';

export const generateNewApplication = (userid?: string): MApplication => {
  return {
    id: 'new',
    ttpf: {
      ttpfId: '',
      ttpfOwnerFirstName: '',
      ttpfOwnerLastName: '',
      notParentTtpfAccount: null,
      ttpfParentSignature: {
        firstName: '',
        lastName: '',
        linkedSignatureProfile: '',
        signed: null,
        signedAgreementWording: null,
        signatureType: EnumAppSignatureType.PARENT,
        signatureFormat: null
      }
    },
    financial: { dependents: 0, income: 0 },
    meta: {
      id: 'new',
      linkedChildProfile: '',
      linkedParentProfile: '',
      status: EnumAppStatus.INCOMPLETE,
      submitted: EnumAppSubmitted.NO,
      userId: userid || '',
      year: dayjs().tz('America/Chicago').month() < 8 ? dayjs().get('year') : dayjs().get('year') + 1,
      progress: { '0': true }
    },
    essay: {
      docContent: undefined,
      docFormat: EnumAppDocFormat.TEXT,
      docStatus: undefined,
      docType: EnumAppDocType.ESSAY
      // essaySignature: {
      //   firstName: '',
      //   lastName: '',
      //   linkedSignatureProfile: '',
      //   signed: null,
      //   signatureType: EnumAppSignatureType.AGREEMENT,
      //   signatureFormat: EnumAppSignatureFormat.DIGITAL
      // }
    },
    transcript: {
      docContent: undefined,
      docFormat: EnumAppDocFormat.FILE,
      docStatus: EnumFileStatus.RECEIVED,
      docType: EnumAppDocType.TRANSCRIPT
    },
    signatures: []
  };
};

export const sortItems = (items: unknown[], sortField = 'id', sortDirection = 'desc'): unknown[] => {
  if (!items) {
    return items;
  }

  return (items as Record<string, unknown>[]).sort((a: Record<string, unknown>, b: Record<string, unknown>): number => {
    let textA: string | number | null;
    let textB: string | number | null;

    switch (typeof a[sortField]) {
      case 'string':
        textA = a[sortField] ? (a[sortField] as string).toUpperCase() : '';
        textB = b[sortField] ? (b[sortField] as string).toUpperCase() : '';
        break;
      case 'number':
        textA = a[sortField] ? (a[sortField] as number) : 0;
        textB = b[sortField] ? (b[sortField] as number) : 0;
        break;
      default:
        throw new Error('Cannot sort by this type of field');
    }

    if (sortDirection === 'asc') {
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    }

    return textA > textB ? -1 : textA < textB ? 1 : 0;
  });
};

/**
 * Update the existing working application
 * @param currentApp
 * @param id
 * @param section
 * @param field
 * @param value
 */
export const updateApplicationField = (
  currentApp: MApplication | undefined,
  id: string,
  section: string,
  field: string,
  value: unknown
): MApplication => {
  if (!currentApp || Object.keys(currentApp).length === 0) {
    console.log('updateApplicationField needs new application!');
    currentApp = generateNewApplication();
  }
  currentApp.id = id;
  const existingSection = currentApp[section as keyof MApplication];
  if (existingSection && typeof existingSection === 'object') {
    if (field === 'root') {
      currentApp[section as keyof MApplication] = value as never;
    } else {
      currentApp[section as keyof MApplication] = {
        ...(existingSection as Record<string, unknown>),
        [field]: value
      } as never;
    }
  }

  return { ...currentApp };
};

export const updateProfileField = (items: MProfile[], id: string, field: string, value: string): MProfile[] => {
  const existingItem = items.find((item) => item.id === id);

  if (existingItem) {
    return items.map((item) => (item.id === id ? { ...item, [field]: value } : item));
  }

  return [...items];
};

export const rebuildTtpf = (payload: Record<string, unknown>): MAppSignature => {
  return {
    firstName: payload.firstName as string | null,
    lastName: payload.lastName as string | null,
    signatureFormat: payload.signatureFormat ? (payload.signatureFormat as EnumAppSignatureFormat) : EnumAppSignatureFormat.DIGITAL,
    signatureType: payload.signatureType ? (payload.signatureType as EnumAppSignatureType) : EnumAppSignatureType.PARENT,
    signed: Object.prototype.hasOwnProperty.call(payload, 'checked')
      ? (payload.checked as boolean | null)
      : (payload.signed as boolean | null),
    signedAgreementWording: payload.signedAgreementWording ? (payload.signedAgreementWording as string) : ''
  };
};

export const upsertItems = (items: (MProfile | MAppMeta)[] | undefined, newItem: MProfile | MAppMeta): (MProfile | MAppMeta)[] => {
  if (!items) {
    items = [];
  }
  const existing = items.find((item) => item.id === newItem.id);
  if (existing) {
    return items.map((item) => (item.id === newItem.id ? newItem : item));
  }

  if (newItem.firstName) {
    return [...items, newItem];
  }

  return items;
};

export const upsertSignatures = (items: MAppSignature[] | undefined, newItem: MAppSignature): MAppSignature[] => {
  if (!items) {
    items = [];
  }
  const existing = items.find((item) => item.signatureId === newItem.signatureId);
  if (existing) {
    return items.map((item) => (item.signatureId === newItem.signatureId ? newItem : item));
  }

  return [...items, newItem];
};

export const removeSignature = (application: MApplication | undefined, signatureId: string): MApplication | undefined => {
  let updatedApp = application;

  const signature = updatedApp?.signatures.find((item) => item.signatureId === signatureId);

  if (signature) {
    if (signature.signatureType === EnumAppSignatureType.PURCHASER) {
      delete updatedApp!.ttpf.ttpfPurchaserSignature;
    }
    updatedApp!.signatures = updatedApp!.signatures.filter((item) => item.signatureId !== signatureId);
  }

  return updatedApp;
};

export const removeProfile = (items: MProfile[], idToRemove: string): MProfile[] => {
  const existingItem = items.find((cartItem) => cartItem.id === idToRemove);

  if (existingItem) {
    return items.filter((cartItem) => cartItem.id !== idToRemove);
  }

  return items;
};

export const removeApplication = (items: MApplication[], idToRemove: string): MApplication[] => {
  return items.filter((cartItem) => cartItem.id !== idToRemove);
};

export const createNewProfile = (existingProfile: MProfile | undefined, profileType: string): MProfile => {
  const newItem: MProfile = {
    address1: '',
    address2: '',
    addressCity: '',
    addressCounty: '',
    addressCountry: 'US',
    addressState: 'TX',
    addressZip: '',
    mailing1: '',
    mailing2: '',
    mailingCity: '',
    mailingCountry: '',
    mailingState: '',
    mailingZip: '',
    dob: null,
    email: '',
    firstName: '',
    hasScholarship: 'no',
    hasMailingAddress: false,
    grade: 0,
    id: 'new',
    lastName: '',
    phone: '',
    userId: ''
  };
  if (profileType === 'parent') {
    newItem.relationship = '';
    newItem.email = '';
    newItem.phone = '';
  }
  if (existingProfile) {
    newItem.address1 = existingProfile.address1;
    newItem.address2 = existingProfile.address2;
    newItem.addressCity = existingProfile.addressCity;
    newItem.addressCountry = existingProfile.addressCountry;
    newItem.addressCounty = existingProfile.addressCounty;
    newItem.addressState = existingProfile.addressState;
    newItem.addressZip = existingProfile.addressZip;
  }

  return newItem;
};
